import orchestration from '../orchestration'
import {
  CONSTANT,
  SDK_CAPITALIZE_NAMES,
  SDK_ENUMS,
  SDK_PAYMENT_OPTIONS,
  SDK_ID_DISCOVER,
  INVISIBLE_CHECKOUT_EXPERIENCE,
  CHECKOUT_METHODS,
  DEFAULT_CHECKOUT_EXPERIENCE,
  SCOF_AUTH_NTS,
  windowRefTypes,
  SCOF_AUTH_ICT,
  SCOF_AUTH_ENC
} from './constant'
import { store } from '../store/store'
import { EventBus } from '../event-bus'
import sharedService from './shared-service'
import api from '../actions/api.actions'
import traceService from '../components/shared/trace/trace-service'
import toasterService from './toaster'
import messages from '../messages'
import keyVerification from './keyVerification'
import encrypt from './encrypt'
import consumerData from './consumer'

const checkout = {

  async initiate ({ cardDetail, enrollCard, srciActionCode = '' }) {
    let consumer
    let srcNetwork = cardDetail.network || cardDetail.paymentCardDescriptor
    if (srcNetwork === 'maestro') {
      srcNetwork = 'mastercard'
    }
    let dpaTransactionOptions = sharedService.getDpaTransactionOptions()
    let profileOptOut = this.getProfileOptOutParam(cardDetail)
    let otherParameters = this.getCheckoutOtherParameters({ cardDetail, enrollCard, srcNetwork, srciActionCode })
    if (localStorage.configDetails && JSON.parse(localStorage.configDetails).checkout.newUser) {
      store.commit('updateNewUser', true)
    }
    enrollCard && await this.encryptCardForCheckout({ cardDetail })
    let checkoutCardDetail = this.getCheckoutCardDetail({ cardDetail })
    if (enrollCard && Object.keys(store.getters.consumerInfo).length) {
      consumer = consumerData.getConsumerLoginDetails(store.getters.consumerInfo) || {}
    }
    this.setLastCheckoutData()
    return orchestration.callMethod({
      methodName: CONSTANT.TRACE.METHOD.CHECKOUT,
      methodParameters: {
        ...otherParameters,
        ...checkoutCardDetail,
        dpaTransactionOptions,
        consumer,
        ...(profileOptOut !== null && { profileOptOut })
      },
      enabledSdks: [srcNetwork]
    }).then(response => {
      store.commit('updateNewUser', false)
      store.dispatch('updateRememberMeConsent', false)
      if (checkoutCardDetail && checkoutCardDetail.encryptedCard) {
        if (response.dcfActionCode === 'COMPLETE') {
          store.commit('updateEncryptedcard', checkoutCardDetail.encryptedCard)
        } else {
          store.commit('updateEncryptedcard', null)
        }
      } else {
        store.commit('updateEncryptedcard', null)
      }
      if (srcNetwork) {
        response.srcNetwork = srcNetwork
      }
      checkout.validateDcfActionCode(response)
    }).catch(error => {
      console.log(error)
      store.commit('updateNewUser', false)
      this.srcWindowHandle(false)
      store.commit('lightBoxloaderOn', false)
      store.commit('checkoutProgress', false)
      throw error
    })
  },

  async initiateAuthUI ({ params, authenticationMethod }) {
    return orchestration.callMethod({
      methodName: CONSTANT.TRACE.METHOD.AUTHENTICATE_CONSUMER,
      methodParameters: {
        params,
        authenticationMethod
      },
      enabledSdks: ['mastercard']
    }).then(response => {
      this.validateAuthUIPopupActionCode(response)
    }).catch(error => {
      console.log(error)
      toasterService.onFailure(error || 'CONSUMER_AUTHENTICATE_FAILED')
      store.commit('lightBoxloaderOn', false)
      this.srcWindowHandle(false)
      window.location.reload()
    })
  },

  validateAuthUIPopupActionCode (response) {
    let originUrl = window.origin
    switch (response.authenticationStatus) {
      case 'COMPLETE':
        if (response.authenticationResult === 'AUTHENTICATED') {
          store.commit('consumerAuthorization', [response.idToken])
          EventBus.$emit('refreshCards', true)
          this.srcWindowHandle(originUrl + '/srci/payment-method')
        }
        if (response.authenticationResult === 'NOT_AUTHENTICATED') {
          this.srcWindowHandle(originUrl + '/srci/register')
        }
        break
      case 'NOT_SUPPORTED':
        this.srcWindowHandle(false)
        window.location.reload()
        break
      case 'CANCELLED':
        this.srcWindowHandle(originUrl + '/srci/register')
        break
      default:
        store.commit('lightBoxloaderOn', false)
        toasterService.onFailure(this.$t('errorMessages.AUTHENTICATE_FAILED'))
    }
  },

  buildPayloadBody (response) {
    let srcNetwork = response.srcNetwork.toLowerCase()
    let checkoutMethod = store.getters.checkoutMethod || 'direct'
    if (checkoutMethod === 'direct') {
      if (srcNetwork === 'visa' || srcNetwork === 'discover') {
        if (response.checkoutResponse) {
          response.checkoutResponseSignature = response.checkoutResponse
          let payloadBody = (response.checkoutResponseSignature).split('.')[1]
          response.checkoutResponse = JSON.parse(atob(payloadBody))
        }
      }
    }
  },

  getCheckoutOtherParameters ({ cardDetail, enrollCard, srcNetwork, srciActionCode }) {
    let otherParameters = {
      network: srcNetwork
    }
    let isRecognized = store.getters.isRecognized
    let merchantName = store.getters.dpaId.displayDpaName
    if (enrollCard) {
      if (isRecognized.recognized && store.getters.checkoutMethod !== CHECKOUT_METHODS.aggregator.name) {
        otherParameters['idToken'] = isRecognized.idTokens[0]
      } else if (!isRecognized) {
        otherParameters['srciActionCode'] = SDK_ENUMS.SrciActionCodes.newUser
      }
    } else {
      otherParameters['idToken'] = cardDetail.idToken
      otherParameters['srcCorrelationId'] = cardDetail.srcCorrelationId
      otherParameters['dpaData'] = { dpaPresentationName: merchantName }
    }

    if (srciActionCode !== '') {
      otherParameters.srciActionCode = srciActionCode
    }

    return otherParameters
  },

  getCheckoutCardDetail ({ cardDetail }) {
    let checkoutCardDetail
    if (cardDetail.srcDigitalCardId) {
      checkoutCardDetail = { 'srcDigitalCardId': cardDetail.srcDigitalCardId }
    } else if (cardDetail.card) {
      checkoutCardDetail = { 'card': cardDetail.card }
    } else {
      checkoutCardDetail = { 'encryptedCard': cardDetail.encryptedCard }
    }
    return checkoutCardDetail
  },

  async encryptCardForCheckout ({ cardDetail }) {
    if (!store.getters.apiEndpoints) {
      toasterService.onFailure(messages['en-US'].errorMessages.SOMETHING_WRONG)
    }
    let srcNetwork = cardDetail.network.toUpperCase()
    if (srcNetwork === 'MAESTRO') {
      srcNetwork = 'MASTERCARD'
    }
    let srcNetworkKeys = store.getters.srcKeys[srcNetwork].keys
    const encParam = {
      encContentAlgo: CONSTANT.PAN_ENC.ENCRYPTION_CONTENT_ALGO,
      encAlgo: CONSTANT.PAN_ENC.ENC_ALGO,
      encKeyId: store.getters.apiEndpoints[srcNetwork].CARD_ENCKEY_KID,
      keys: srcNetworkKeys
    }
    delete cardDetail.network
    if (store.getters.billingAddress !== 'NONE') {
      let isBillingAddressPresent = this.checkBillingAddressProperties(store.getters.consumerBillingAddress)
      if (!isBillingAddressPresent) {
        cardDetail.billingAddress = store.getters.consumerBillingAddress
      }
    }
    // TODO: need to remove this console further. its just for testing for now
    console.log('Card detail getting passed in encryption', cardDetail)
    let encryptedCard = await encrypt.encryptCard(cardDetail, encParam)
    cardDetail.encryptedCard = encryptedCard
  },

  checkBillingAddressProperties (obj) {
    for (let key in obj) {
      if (obj[key] !== null && obj[key] !== '') { return false }
    }
    return true
  },

  /* eslint complexity: ["error", 10] */
  /**
   * validate dcf action code
   * @param checkoutResponse
   */
  validateDcfActionCode (checkoutResponse) {
    console.log(checkoutResponse)
    let originUrl = window.origin
    checkoutResponse.unbindAppInstance && this.unbindAppInstance(checkoutResponse.idToken)
    switch (checkoutResponse.dcfActionCode) {
      case 'CANCEL':
        this.srcWindowHandle(false)
        window.location.reload()
        break
      case 'COMPLETE':
        this.buildPayloadBody(checkoutResponse)
        this.updateLastCheckoutData(checkoutResponse)
        this.dcfActionCodeComplete(checkoutResponse)
        this.setRecognitionTokenFromCheckout(checkoutResponse)
        break
      case 'CHANGE_CARD':
        this.dcfActionCodeChangeCard(originUrl)
        break
      case 'ADD_CARD':
        EventBus.$emit('refreshCards', true)
        this.srcWindowHandle(originUrl + '/srci/payment-method')
        break
      case 'SWITCH_CONSUMER':
        store.dispatch('recognized', false).then(() => {
          store.commit('checkoutProgress', false)
          this.srcWindowHandle(originUrl + '/srci/register')
          EventBus.$emit('switchConsumer')
        })
        this.setRecognitionTokenFromCheckout(checkoutResponse)
        if (store.getters.windowRefType === windowRefTypes.ACTION_SHEET) {
          window.location.reload()
        }
        break
      case 'ERROR':
        this.srcWindowHandle(false)
        break
      default:
        this.srcWindowHandle(false)
    }
  },

  /**
   * This function will call when click on cancel
   */
  dcfActionCodeChangeCard (originUrl) {
    if (store.getters.windowRefType === windowRefTypes.ACTION_SHEET) {
      this.srcWindowHandle(false)
      window.location.reload()
    }
    if (store.getters.selectedCheckoutExperience !== DEFAULT_CHECKOUT_EXPERIENCE && !store.getters.isRecognized) {
      store.commit('checkoutProgress', false)
    } else {
      EventBus.$emit('refreshCards', true)
      this.srcWindowHandle(originUrl + '/srci/payment-method')
    }
  },

  /**
   * This function will be get SRC payload and init SRC
   * after one payment completed
   * @param checkoutResponse
   */
  dcfActionCodeComplete (checkoutResponse) {
    let srcNetwork = checkoutResponse.srcNetwork.toUpperCase()
    let srcNetworkKeys = store.getters.srcKeys[srcNetwork].keys
    let data = keyVerification.verifyKey(checkoutResponse, srcNetworkKeys)
    if (data) {
      this.getSrcPayload(checkoutResponse)
    }
  },

  setRecognitionTokenFromCheckout (checkoutResponse) {
    if (checkoutResponse.recognitionToken) {
      let recognitionToken = store.getters.recognitionToken
      let environment = store.getters.environment
      recognitionToken[environment] = checkoutResponse.recognitionToken
      if (window.opener && window.opener.childSrcWindow) {
        window.opener.postMessage({ recognitionToken: recognitionToken })
      }
      store.commit('setRecognitionToken', recognitionToken)
    }
  },

  /**
   * Get SRC Payload for Mastercard
   * @param checkoutData
   * @returns {Promise<void>}
   */
  getSrcPayload (checkoutData) {
    const srcCorrelationId = checkoutData.checkoutResponse.srcCorrelationId
    const srcDigitalCardId = checkoutData.checkoutResponse.maskedCard.srcDigitalCardId
    const srcNetwork = checkoutData.srcNetwork.toUpperCase()
    if (store.state.configuration.cofOnOff) {
      store.commit('updateCOFSrciTxId', checkoutData.checkoutResponse.srciTransactionId)
      store.commit('updateCOFDigitalCardId', srcDigitalCardId)
    } else {
      store.commit('updateCOFSrciTxId', '')
      store.commit('updateCOFDigitalCardId', '')
    }
    store.commit('srcNetwork', srcNetwork)
    store.commit('updateSRCCorrelationId', srcCorrelationId)
    this.callSrcPayLoad(srcCorrelationId, srcDigitalCardId, srcNetwork)
  },

  /**
   * call src payload for AMEX, VISA and MC
   * @param srcCorrelationId
   * @param srcDigitalCardId
   * @param srcNetwork
   * @returns {Promise<void>}
   */
  async callSrcPayLoad (srcCorrelationId, srcDigitalCardId, srcNetwork, isSaveCardOnFile, scofDpaId = null) {
    let srcApiInputs = {}
    srcApiInputs.standardSrcInputs = sharedService.getStandardSrcInputs(srcCorrelationId, srcNetwork)
    srcApiInputs.standardSrcInputs['region'] = CONSTANT.REGION.GLOBAL
    srcApiInputs.payloadInputs = this.getPayloadSrcInputs(srcDigitalCardId, srcNetwork)
    if (isSaveCardOnFile) {
      srcApiInputs.payloadInputs.isSaveCardOnFile = true
      srcApiInputs.standardSrcInputs.srcDsaId = scofDpaId
    }
    delete srcApiInputs.payloadInputs.dpaTransactionOptions.customInputData
    api.getSrcPayload(srcApiInputs).then(response => {
      let decryptedPayloadResponse = response.payloadOutputs.decryptedPayload.payload
      let decryptedPayload = response.payloadOutputs[CONSTANT.PAYLOAD_KEY[srcNetwork]] || {}
      let maskedCard = decryptedPayload.maskedCard || {}
      decryptedPayloadResponse.artUri = maskedCard.digitalCardData.artUri || ''
      decryptedPayloadResponse.assuranceData = decryptedPayload.assuranceData || ''
      this.updateDecryptedResponse(decryptedPayloadResponse)
      traceService.updateCallTraces(srcNetwork, response.callTraces, true)
      store.commit('updateEnableConfirmation', true)
      if (!isSaveCardOnFile) {
        this.routeToCallBackPage()
      }
      store.commit('loaderOn', false)
    }).catch(error => {
      console.log(error)
      let errorLog = ((((error || {}).response || {}).data || {}).callTraces) ||
        this.serviceDownError(srcNetwork, error, srcApiInputs)
      toasterService.onFailure(messages['en-US'].errorMessages.PAYLOAD_ERROR)
      traceService.updateCallTraces(srcNetwork, errorLog, false)
      let pageInvokedFrom = this.getPageInvokedFrom()
      if (pageInvokedFrom === CONSTANT.SRC.CART || pageInvokedFrom === CONSTANT.SRC.INLINE_CARD) {
        window.location.reload()
      }
      this.srcWindowHandle(false)
      store.commit('loaderOn', false)
    })
  },

  /**
   * call enroll
   * @param srcCorrelationId
   * @param payloadResponse
   * @param dpaId
   * @param encryptedcard
   * @param srcNetwork
   */
  async callEnroll (srcCorrelationId, payloadResponse, encryptedcard, dpaId, srcNetwork) {
    let apiInputs = {}
    apiInputs.standardSrcInputs = sharedService.getStandardSrcInputs(srcCorrelationId, srcNetwork)
    store.commit('setSrcClientId', apiInputs.standardSrcInputs.srciClientId)
    delete apiInputs.standardSrcInputs.srcDsaId
    delete apiInputs.standardSrcInputs.srcNetwork
    delete apiInputs.standardSrcInputs.srcTransactionId
    delete apiInputs.standardSrcInputs.srciType
    delete apiInputs.standardSrcInputs.srcRecipientKeyId
    apiInputs.standardSrcInputs.srcDpaId = dpaId

    // Set flowType
    this.setFlowType(apiInputs)

    if (payloadResponse !== null) {
      apiInputs.standardSrcInputs['region'] = CONSTANT.REGION.GLOBAL
      apiInputs.scofInputs = sharedService.getScofInputs(payloadResponse)
      apiInputs.scofInputs.encryptedCard = encryptedcard
    } else {
      apiInputs.standardSrcInputs['region'] = store.getters.region
      apiInputs['scofInputs'] = {
        encryptedCard: encryptedcard,
        cardSource: 'MERCHANT',
        ...(store.getters.enableTokenizationConsent && sharedService.checkFeatureFlag('START_ICT_CHANGES') && { ...store.getters.complianceSettingsCITObj }),
        ...(!(store.getters.enableENCFlow &&
          sharedService.checkFeatureFlag('START_ENC_CHANGES')) &&
          {
            consumer: {
              consumerIdentity: {
                identityType: 'EXTERNAL_ACCOUNT_ID',
                identityValue: store.getters.consumerInfo.email
              }
            }
          })
      }
    }
    return new Promise((resolve, reject) => {
      api.enrollCard(apiInputs).then(response => {
        traceService.updateCallTraces(srcNetwork, response.callTraces, true)
        EventBus.$emit('enrollCardResponse', response.scofOutputs)
        resolve(response.scofOutputs)
      }).catch((error) => {
        store.commit('loaderOn', false)
        console.log(error)
        traceService.updateCallTraces(srcNetwork, error.response.data.callTraces, true)
        reject(error)
      })
    })
  },

  /**
   * Determines and sets the appropriate flow type in standardSrcInputs
   * based on feature flags and flow enablement conditions.
   *
   * @param {Object} apiInputs - The API input object containing standardSrcInputs.
   */
  setFlowType (apiInputs) {
    if (store.getters.isNTSFlowEnabled && sharedService.checkFeatureFlag('START_NTS_CHANGES') && store.getters.enableNativeIntegrationFlow) {
      apiInputs.standardSrcInputs['flowType'] = SCOF_AUTH_NTS
      delete apiInputs.standardSrcInputs.srcDpaId
    }

    if (store.getters.enableTokenizationConsent && sharedService.checkFeatureFlag('START_ICT_CHANGES')) {
      apiInputs.standardSrcInputs['flowType'] = SCOF_AUTH_ICT
    }

    if (store.getters.enableENCFlow && sharedService.checkFeatureFlag('START_ENC_CHANGES')) {
      apiInputs.standardSrcInputs['flowType'] = SCOF_AUTH_ENC
    }
  },

  /**
   * error message to be displayed when service is down
   * @param srcNetwork
   * @param error
   * @param requestData
   * @param api
   * @returns {{responseBody: *, requestBody: *, url: *}[]}
   */
  serviceDownError (srcNetwork, error, requestData) {
    let errorMsg, payloadUrl, serviceDownMsg, paymentProcessorDetail, api, errorStatus
    errorStatus = (error.response || {}).status || ''
    api = messages['en-US'].errorMessages.SERVICE_DOWN_ERROR.PAYLOAD
    serviceDownMsg = messages['en-US'].errorMessages.SERVICE_DOWN_ERROR
    errorMsg = errorStatus + serviceDownMsg.STATUS_RECEIVED + serviceDownMsg.TRANSACTION_ID + store.getters.srcTransactionId + serviceDownMsg.DETAILS
    paymentProcessorDetail = store.state.configuration.advanced.paymentProcessor[srcNetwork]
    payloadUrl = paymentProcessorDetail.API_HOST + (api === serviceDownMsg.PAYLOAD ? paymentProcessorDetail.PAYLOAD_PATH : paymentProcessorDetail.CONFIRMATION_PATH)
    return [{ responseBody: JSON.stringify({ error: errorMsg }), url: payloadUrl, requestBody: requestData }]
  },

  /**
   * Get SRC inputs for payload
   * @param srcDigitalCardIdOnCheckout
   * @returns {{isCardOnFile: *, srciTransactionId: *, srcDigitalCardId: *}}
   */
  getPayloadSrcInputs (srcDigitalCardIdOnCheckout, srcNetwork) {
    let srcDigitalCardId
    if (srcDigitalCardIdOnCheckout && srcDigitalCardIdOnCheckout.length > 0) {
      srcDigitalCardId = srcDigitalCardIdOnCheckout
    } else {
      srcDigitalCardId = store.state.configuration.advanced.cofDigitalCardId
    }
    const dpaTransactionOptions = sharedService.getDpaTransactionOptions()
    if (!dpaTransactionOptions.threeDsPreference) {
      dpaTransactionOptions.threeDsPreference = 'NONE'
    }
    dpaTransactionOptions.paymentOptions = checkout.getPaymentOptionsByNetwork(srcNetwork.toLowerCase())
    const requestBody = {
      'isCardOnFile': store.state.configuration.cofOnOff,
      'srcDigitalCardId': srcDigitalCardId,
      'srciTransactionId': store.state.configuration.advanced.cofSrciTxId,
      'dpaTransactionOptions': dpaTransactionOptions,
      'payloadTypeIndicatorCheckout': store.getters.checkoutType !== 'NONE' ? 'FULL' : store.getters.payloadTypeIndicatorCheckout,
      'payloadTypeIndicatorPayload': store.getters.payloadTypeIndicatorPayload
    }
    return requestBody
  },

  /**
   * Return to callback or cart page after successful checkout
   */
  routeToCallBackPage () {
    sharedService.updateOnSrciPopupClose()
    let pageInvokedFrom = this.getPageInvokedFrom()

    if (pageInvokedFrom !== INVISIBLE_CHECKOUT_EXPERIENCE && !store.getters.cofOnOff) {
      this.srcWindowHandle(false)
    }

    if (pageInvokedFrom === CONSTANT.SRC.CONFIGURATION) {
      window.location.replace(CONSTANT.CALLBACK_ROUTE)
    } else {
      window.location.replace('/review')
    }
  },

  /**
   * update decrypted response
   * @param decryptedResponse
   */
  updateDecryptedResponse (decryptedResponse) {
    store.commit('updateDecryptedPayload', decryptedResponse)
  },

  /**
   * Navigate to register screen on click of not you
   */
  srcWindowHandle (url) {
    if (this.getPageInvokedFrom() === CONSTANT.SRC.INLINE_CARD || !url) {
      sharedService.updateOnSrciPopupClose()
      window.childSrcWindow && window.childSrcWindow.close()
    } else {
      window.childSrcWindow && window.childSrcWindow.location.replace(url)
    }
  },

  getPageInvokedFrom () {
    return localStorage.getItem('invokedFrom')
  },

  setLastCheckoutData () {
    store.commit('updateEnableConfirmation', false)
    let initiatorAndDpaIds = {
      initiatorAndDpaIds: sharedService.getAllInitiatorAndDpaIds()
    }
    localStorage.setItem('lastCheckoutData', JSON.stringify(initiatorAndDpaIds))
  },

  updateLastCheckoutData (checkoutResponse) {
    let lastCheckoutData = JSON.parse(localStorage.getItem('lastCheckoutData') || '{}')
    lastCheckoutData['checkoutResponse'] = Object.assign({}, checkoutResponse)
    localStorage.setItem('lastCheckoutData', JSON.stringify(lastCheckoutData))
  },

  /**
   * Call Unbind App Instance SDK
   */
  unbindAppInstance (idToken) {
    if (typeof idToken !== 'string') {
      idToken = ((idToken || [])[0]) || ''
    }
    orchestration.callMethod({ methodName: CONSTANT.TRACE.METHOD.UNBIND_APP_INSTANCE, methodParameters: { idToken } }).then(response => {
      let recognitionToken = store.getters.recognitionToken
      let environment = store.getters.environment
      delete recognitionToken[environment]
      if (window.opener && window.opener.childSrcWindow) {
        window.opener.postMessage({ recognitionToken: recognitionToken })
      }
      store.commit('setRecognitionToken', recognitionToken)
    })
  },

  getPaymentOptionsByNetwork (sdkName) {
    let sdkParameters = SDK_PAYMENT_OPTIONS[sdkName]
    let paymentOptions = {}
    if (sdkParameters.includes('dpaDynamicDataTtlMinutes')) {
      paymentOptions.dpaDynamicDataTtlMinutes = store.getters.validityPeriodMinutes
    }
    if (sdkParameters.includes('dpaPanRequested')) {
      paymentOptions.dpaPanRequested = false
    }
    if (sdkParameters.includes('dpaDynamicDataTtlMinutes')) {
      paymentOptions.dynamicDataType = store.getters.dynamicDataTypeByNetwork(SDK_CAPITALIZE_NAMES[sdkName])
    }
    if (sdkName === SDK_ID_DISCOVER) {
      paymentOptions = [paymentOptions]
    }
    return paymentOptions
  },

  getProfileOptOutParam (cardDetail) {
    let profileOptOut = null
    if (cardDetail.hasOwnProperty('profileOptOut')) {
      profileOptOut = !cardDetail?.profileOptOut
      delete cardDetail?.profileOptOut
    }
    return profileOptOut
  }
}

export default checkout
